/**
 * Error handling utilities for the SpeedGauge Copilot client.
 * 
 * Provides helper functions to categorize and handle different error types,
 * with special handling for session expiration and authentication errors.
 */

export interface ErrorInfo {
  type: 'session_expired' | 'auth_failed' | 'network_error' | 'server_error' | 'unknown';
  message: string;
  isRecoverable: boolean;
  suggestedAction?: string;
  originalError?: any;
}

/**
 * Determine if an error is due to session expiration.
 * 
 * Session expiration errors typically come from 404 responses with
 * messages indicating the session was not found or expired.
 */
export function isSessionExpired(error: any): boolean {
  if (!error) return false;

  // Check for HTTP 404 with session expiration keywords
  if (error.response?.status === 404) {
    const detail = error.response?.data?.detail || '';
    if (
      detail.toLowerCase().includes('session') ||
      detail.toLowerCase().includes('expired') ||
      detail.toLowerCase().includes('inactivity')
    ) {
      return true;
    }
  }

  // Check error message directly
  if (typeof error.message === 'string') {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('session') &&
      (msg.includes('not found') || msg.includes('expired'))
    );
  }

  return false;
}

/**
 * Determine if an error is due to authentication failure.
 */
export function isAuthenticationError(error: any): boolean {
  if (!error) return false;

  // Check for HTTP 401 - but not if it's actually a session expiration
  if (error.response?.status === 401) {
    // Make sure it's not a session expiration error
    const detail = error.response?.data?.detail || '';
    if (
      detail.toLowerCase().includes('session') ||
      detail.toLowerCase().includes('expired') ||
      detail.toLowerCase().includes('inactivity')
    ) {
      return false; // This is a session issue, not auth
    }
    return true;
  }

  // Check for auth-related error messages
  if (typeof error.message === 'string') {
    const msg = error.message.toLowerCase();
    return msg.includes('authentication') || msg.includes('unauthorized');
  }

  return false;
}

/**
 * Determine if an error is due to network issues.
 */
export function isNetworkError(error: any): boolean {
  if (!error) return false;

  // Check for network error codes
  if (error.code?.includes('ERR_NETWORK')) {
    return true;
  }

  // Check for connection-related error messages
  if (typeof error.message === 'string') {
    const msg = error.message.toLowerCase();
    return (
      msg.includes('network') ||
      msg.includes('connection') ||
      msg.includes('timeout')
    );
  }

  // No response status indicates network error
  if (!error.response) {
    return true;
  }

  return false;
}

/**
 * Categorize an error and provide actionable information.
 */
export function categorizeError(error: any): ErrorInfo {
  if (isSessionExpired(error)) {
    return {
      type: 'session_expired',
      message:
        'Session not found or has expired due to inactivity. Please log in again to continue.',
      isRecoverable: true,
      suggestedAction: 'login',
      originalError: error
    };
  }

  if (isAuthenticationError(error)) {
    return {
      type: 'auth_failed',
      message:
        error.response?.data?.detail ||
        'Authentication failed. Please log in again.',
      isRecoverable: true,
      suggestedAction: 'login',
      originalError: error
    };
  }

  if (isNetworkError(error)) {
    return {
      type: 'network_error',
      message:
        'Network connection failed. Please check your internet connection and try again.',
      isRecoverable: true,
      suggestedAction: 'retry',
      originalError: error
    };
  }

  // Check for server errors (5xx status codes)
  if (error.response?.status >= 500) {
    const serverMessage = error.response?.data?.detail;
    return {
      type: 'server_error',
      message:
        serverMessage ||
        'Server error. Please try again later.',
      isRecoverable: true,
      suggestedAction: 'retry',
      originalError: error
    };
  }

  // Check for specific HTTP errors with server-provided detail
  if (error.response?.status >= 400) {
    const serverMessage = error.response?.data?.detail;
    return {
      type: 'unknown',
      message:
        serverMessage ||
        `Server error (${error.response?.status}). Please try again.`,
      isRecoverable: true,
      suggestedAction: 'retry',
      originalError: error
    };
  }

  // Default to generic error
  return {
    type: 'unknown',
    message:
      error.message ||
      'An unexpected error occurred. Please try again.',
    isRecoverable: true,
    suggestedAction: 'retry',
    originalError: error
  };
}

/**
 * Get a user-friendly message for an error that includes a helpful hint.
 */
export function getErrorMessageWithHint(error: any): {
  message: string;
  hint?: string;
} {
  const errorInfo = categorizeError(error);

  let hint: string | undefined;

  switch (errorInfo.type) {
    case 'session_expired':
      hint =
        'Your session timed out after 24 hours of inactivity. This is normal for security purposes.';
      break;

    case 'auth_failed':
      hint =
        'Make sure your login credentials are correct and try again.';
      break;

    case 'network_error':
      hint =
        'Check that you have a stable internet connection and the SpeedGauge server is accessible.';
      break;

    case 'server_error':
      hint =
        'The server may be temporarily unavailable. Try again in a few moments.';
      break;

    default:
      hint = undefined;
  }

  return {
    message: errorInfo.message,
    hint
  };
}
